import { applyCleValidationErrorsToFormik } from "@/api/cle-api/errors";
import Button from "@/components/bricks/button/Button";
import { CleApiErrorAlert } from "@/components/cle-api/CleApiErrorAlert";
import ValidationErrorMessage from "@/components/formcontrols/ValidationErrorMessage";
import EditIcon from "@/components/icons/EditIcon";
import Modal, {
  ModalContext,
  ModalProvider,
  ModalTrigger,
} from "@/components/Modal/Modal";
import Spinner from "@/components/spinner/Spinner";
import { StickyFooter } from "@/components/stickyFooter/modalStickyFooter";
import type {
  ReductionEvidenceModel,
  UpdateReductionRequest,
} from "@/api/cle-api";
import { useUpdateReduction } from "@/hooks/cle-hooks";
import { Form, FormikProvider, useFormik } from "formik";
import { useContext, useMemo } from "react";
import * as Yup from "yup";
import { YupNumberOfLessonsNullable } from "../../../util/yup";

export interface ReductionEditFormValues {
  reason: string;
  numberOfLessons: string;
}

const validationSchema = Yup.object({
  reason: Yup.string().trim(),
  numberOfLessons: YupNumberOfLessonsNullable(),
});

export const reductionToFormValues = (
  reduction: ReductionEvidenceModel,
): ReductionEditFormValues => ({
  reason: reduction.reason ?? "",
  numberOfLessons:
    reduction.numberOfLessons != null ? String(reduction.numberOfLessons) : "",
});

export const formValuesToUpdateRequest = (
  values: ReductionEditFormValues,
  initialValues: ReductionEditFormValues,
): UpdateReductionRequest => {
  const body: UpdateReductionRequest = {};

  const reason = values.reason.trim() || null;
  const initialReason = initialValues.reason.trim() || null;
  if (reason !== initialReason) {
    body.reason = reason;
  }

  const numberOfLessons =
    values.numberOfLessons === "" ? undefined : Number(values.numberOfLessons);
  const initialNumberOfLessons =
    initialValues.numberOfLessons === ""
      ? undefined
      : Number(initialValues.numberOfLessons);
  if (numberOfLessons !== initialNumberOfLessons) {
    body.number_of_lessons = numberOfLessons;
  }

  return body;
};

interface ReductionEditFormProps {
  reduction: ReductionEvidenceModel;
  onSave: (updated: ReductionEvidenceModel) => void;
}

const ReductionEditForm = ({ reduction, onSave }: ReductionEditFormProps) => {
  const { setModalOpen } = useContext(ModalContext);
  const {
    mutate: updateReduction,
    isPending,
    isError,
    error,
    reset: resetMutation,
  } = useUpdateReduction();

  const initialFormValues = useMemo(
    () => reductionToFormValues(reduction),
    [reduction],
  );

  const formik = useFormik<ReductionEditFormValues>({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const body = formValuesToUpdateRequest(values, initialFormValues);
      if (Object.keys(body).length === 0) return;

      updateReduction(
        {
          reductionId: reduction.id,
          userId: reduction.ownerUserId,
          body,
        },
        {
          onSuccess: (updated) => {
            onSave(updated);
            resetMutation();
            setModalOpen(false);
          },
          onError: (err) => {
            applyCleValidationErrorsToFormik(err, formik.setFieldError);
          },
        },
      );
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    resetMutation();
    setModalOpen(false);
  };

  const numberOfLessonsInvalid =
    formik.touched.numberOfLessons && formik.errors.numberOfLessons;

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <div className="c-modal-container">
          <div className="c-modal-container-inner">
            <div className="rounded border bg-light p-3 mb-2">
              <div className="text-muted small mb-1">Reduktion</div>
              <div className="fw-semibold text-break">
                {reduction.fileName ?? "Ukendt fil"}
              </div>
            </div>

            <div className="row g-4">
              <div className="col-sm-5">
                <label className="form-label" htmlFor="numberOfLessons">
                  Antal lektioner
                </label>
                <div className="input-group w-100">
                  <input
                    autoComplete="off"
                    className={` form-control${
                      numberOfLessonsInvalid ? " is-invalid" : ""
                    }`}
                    id="numberOfLessons"
                    min="0"
                    name="numberOfLessons"
                    step="any"
                    type="number"
                    value={formik.values.numberOfLessons}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  />
                  <span className="input-group-text">lektioner</span>
                </div>
                <div className="form-text">Max 54 lektioner.</div>
                <ValidationErrorMessage
                  touched={formik.touched.numberOfLessons}
                  errorMessage={formik.errors.numberOfLessons}
                />
              </div>

              <div className="col-12">
                <label className="form-label" htmlFor="reason">
                  Begrundelse
                </label>
                <textarea
                  className="form-control"
                  id="reason"
                  name="reason"
                  placeholder="Beskriv begrundelsen for reduktionen..."
                  rows={5}
                  value={formik.values.reason}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </div>
            </div>
          </div>
          {isError && (
            <div className="c-modal-container-inner">
              <CleApiErrorAlert
                error={error}
                fallback="Kunne ikke gemme opdatering."
              />
            </div>
          )}
          <StickyFooter className="border-top">
            <Button color="secondary" type="button" onClick={handleCancel}>
              Annuller
            </Button>
            <div className="d-flex gap-2 align-items-center">
              {isPending && <Spinner size={24} />}
              <Button
                color="primary"
                disabled={isPending || !formik.dirty}
                type="submit"
              >
                Gem
              </Button>
            </div>
          </StickyFooter>
        </div>
      </Form>
    </FormikProvider>
  );
};

interface ReductionEditModalProps {
  reduction: ReductionEvidenceModel;
  onSave: (updated: ReductionEvidenceModel) => void;
}

export const ReductionEditModal = ({
  reduction,
  onSave,
}: ReductionEditModalProps) => (
  <ModalProvider>
    <ModalTrigger className="btn btn-primary btn--transparent has-icon px-2 edit-trigger">
      Redigér
      <EditIcon height={16} />
    </ModalTrigger>
    <Modal title="Redigér reduktion">
      <ReductionEditForm reduction={reduction} onSave={onSave} />
    </Modal>
  </ModalProvider>
);
